import { useContext } from "react";
import {
	Box,
	Button,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { useFields } from "../../../../api/useFields";
import {
	countStaffUnavailableSessionsForService,
	customScrollbar,
	getColumnsNames,
	getLabelByServiceId,
	getSessionDatesOfServiceProvided,
	getSessionsOfServiceProvided,
	lastestBookedValidationColor,
	SERVICES_PROVIDED_ENTITY,
} from "../utils";
import { useReservedSlotsByServiceProvided } from "../../../reserved-slots/hooks/useReservedSlotsByServiceProvided";
import { Session } from "../../../../types/records.types";
import { findField } from "../../../../utils/findField";
import { GroupsTitle } from "../../../../components/groups/GroupsTitle";
import { useStore } from "../../../../store/store";
import { AppDialog } from "../../../../store/store.types";
import { ServicesProvidedDialog } from "../treatment-prescription-input/ServicesProvidedDialog";
import { ReservedSlotsDialog } from "../../../reserved-slots/dialog/ReservedSlotsDialog";
import { AddCircle as AddCircleIcon, Edit as EditIcon, Schedule as ScheduleIcon } from "@mui/icons-material";
import { RecordContext } from "../../../../components/record/Record";
import { CreatedByUserTableCell } from "./services-provided-table/CreatedByUserTableCell";
import { BaseInfoTableCells } from "./services-provided-table/BaseInfoTableCells";
import { AppointmentFulfillmentTableCell, PreviousPeriodAppointmentFulfillmentTableCell } from "./services-provided-table/AppointmentFulfillmentTableCell";
import { DoneVsExpectedTableCell } from "./services-provided-table/DoneVsExpectedTableCell";
import {
	SessionDatesInServiceProvided,
	ServiceProvidedWithItsSessions,
	TreatmentPrescription,
	ServiceProvided,
	Treatment,
} from "../types";
import { DatesTableCells } from "./services-provided-table/DatesTableCells";
import { ServiceProvidedStatusTableCell } from "./services-provided-table/ServiceProvidedStatusTableCell";
import { ReservedSlotsTableCell } from "../../../reserved-slots/table/ReservedSlotsTableCell";
import { makeDoneVsExpectedSessionOverall } from "../../../../api/useTreatmentsProvidedAppointments";

type ServicesProviderViewProps = { treatmentPrescription: TreatmentPrescription };
export const ServicesProvidedView = ({ treatmentPrescription }: ServicesProviderViewProps) => {
	const { formatedFields } = useFields(SERVICES_PROVIDED_ENTITY);
	const fieldSessionPeriodicity = findField(formatedFields, "session_periodicity");
	const { appDialog } = useStore();
	const treatment = useContext(RecordContext);

	const serviceProvidedIds = treatmentPrescription?.[SERVICES_PROVIDED_ENTITY]?.map((sp) => sp.id) ?? [];
	const { reservedSlotsByServiceProvided } = useReservedSlotsByServiceProvided(serviceProvidedIds);

	if (!treatment?.id || !treatmentPrescription || !Array.isArray(formatedFields)) return null;

	const handleServicesProvided = (appDialog: AppDialog) => {
		return (selectedServiceProvidedId?: number) => {
			appDialog.createDialog({
				title: "Frequência do Serviço",
				allowSoftCancel: true,
				maxWidth: "md",
				contentAndActions: (
					<ServicesProvidedDialog
						treatmentId={Number(treatment.id)}
						treatmentPrescription={treatmentPrescription}
						selectedServiceProvidedId={selectedServiceProvidedId}
					/>
				),
			});
		};
	};

	const handleReservedSlots = (appDialog: AppDialog) => {
		return (serviceProvided: ServiceProvided) => {
			appDialog.createDialog({
				title: "Horários Reservados",
				allowSoftCancel: true,
				maxWidth: "lg",
				contentAndActions: (
					<ReservedSlotsDialog
						serviceProvided={serviceProvided}
						treatment={treatment.item as Treatment}
					/>
				),
			});
		};
	};

	const servicesProvided = [...treatmentPrescription?.[SERVICES_PROVIDED_ENTITY]].sort((a, b) => {
		const serviceIdDiff = (a.service?.id ?? 0) - (b.service?.id ?? 0); // primary: service.id (asc)
		if (serviceIdDiff !== 0) return serviceIdDiff;
		const dateDiff = new Date(b.date_begin!).getTime() - new Date(a.date_begin!).getTime(); // tie-breaker: date_begin (desc)
		return dateDiff;
	});
	const sessions: Array<Session> = treatmentPrescription?.treatment?.sessions;

	const columnNames = getColumnsNames(
		formatedFields,
		["Serviço", "Data de Início", "Data de Fim", "Estado"],
		[
			"Horários Reservados",
			"Próxima Sessão",
			"Agendamento C. Período Anterior",
			"Agendamento Cumprido Atual",
			"Realizadas/ Esperadas",
			"Primeira Realizada",
			"Última Realizada",
			"Criado Por",
			"Ações",
		],
		["service", "date_begin", "date_end"]
	);

	return (
		<Box p={4}>
			<Box display="flex" flexDirection="column">
				<GroupsTitle title="Serviços Prestados" />
				<DoneVsExpectedOverall treatment={treatment.item} servicesProvided={servicesProvided} />
				<TableContainer component={Paper} sx={{ mt: 6, ...customScrollbar }}>
					<Table
						size="small"
						aria-label={`service-provided`}
						sx={{
							"& td, & th": {
								fontSize: "1rem",
							},
							"& th": { color: "primary.main" },
						}}
					>
						<TableHead>
							<TableRow>
								{columnNames.map((column) => (
									<TableCell
										align={
											["Serviço", "Data Início", "Data Fim"].includes(column ?? "")
												? "left"
												: "center"
										}
										key={column}
									>
										{column === "Agendamento Período Anterior" ? (
											<Tooltip title="Consoante a periodicidade, mostra o agendamento cumprido da semana/mês anterior">
												<span>{column}</span>
											</Tooltip>
										) : column === "Agendamento Cumprido" ? (
											<Tooltip title="Consoante a periodicidade, mostra o agendamento cumprido da semana/mês atual">
												<span>{column}</span>
											</Tooltip>
										) : (
											column
										)}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{servicesProvided.map((serviceProvided, index) => {
								const backgroundColor = index % 2 === 0 ? "transparent" : "#f9f9f9";

								const sessionsInServiceProvided: ServiceProvidedWithItsSessions =
									getSessionsOfServiceProvided(sessions, serviceProvided);

								const sessionsDatesInServiceProvided: SessionDatesInServiceProvided = {
									serviceProvidedId: serviceProvided.id,
									serviceId: serviceProvided.service.id,
									sessionDates: getSessionDatesOfServiceProvided(sessionsInServiceProvided),
								};

								const staffUnavailableCount = countStaffUnavailableSessionsForService(sessions, serviceProvided);

								return (
									<TableRow
										key={serviceProvided.id}
										sx={{
											backgroundColor,
											"&:last-child td, &:last-child th": { border: 0 },
										}}
									>
										<TableCell align="left" sx={{ minWidth: "150px" }}>
											{serviceProvided.service.name}
										</TableCell>

										<DatesTableCells serviceProvided={serviceProvided} />
										<ServiceProvidedStatusTableCell serviceProvided={serviceProvided} />
										<BaseInfoTableCells
											fieldSessionPeriodicity={fieldSessionPeriodicity}
											serviceProvided={serviceProvided}
										/>
										<ReservedSlotsTableCell reservedSlots={reservedSlotsByServiceProvided[serviceProvided.id]} serviceProvided={serviceProvided} />
										<TableCell
											align="center"
											sx={{
												color: lastestBookedValidationColor(
													sessionsDatesInServiceProvided.sessionDates.latestBookedSession
												),
											}}
										>
											{sessionsDatesInServiceProvided.sessionDates?.latestBookedSession}
										</TableCell>
									<PreviousPeriodAppointmentFulfillmentTableCell
										currentServiceSessions={sessionsInServiceProvided.sessions}
										serviceProvided={serviceProvided}
									/>
									<AppointmentFulfillmentTableCell
										currentServiceSessions={sessionsInServiceProvided.sessions}
										serviceProvided={serviceProvided}
									/>
										<DoneVsExpectedTableCell
											currentServiceSessions={sessionsInServiceProvided.sessions}
											serviceProvided={serviceProvided}
											staffUnavailableCount={staffUnavailableCount}
										/>
										<TableCell align="center">
											{sessionsDatesInServiceProvided.sessionDates?.oldestDate}
										</TableCell>
										<TableCell align="center">
											{sessionsDatesInServiceProvided.sessionDates?.mostRecentDate}
										</TableCell>
										<CreatedByUserTableCell serviceProvided={serviceProvided} />
										<TableCell width={"80px"}>
											<Box display="flex" gap={0.5}>
											<IconButton
												onClick={() => handleServicesProvided(appDialog)(serviceProvided.id)}
												size="small"
												color="primary"
													title="Editar Serviço"
											>
												<EditIcon />
											</IconButton>
												<IconButton
													onClick={() => handleReservedSlots(appDialog)(serviceProvided)}
													size="small"
													color="secondary"
													title="Horários Reservados"
												>
													<ScheduleIcon />
												</IconButton>
											</Box>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
			<Button
				fullWidth
				sx={{ mt: 4, maxWidth: "400px" }}
				onClick={() => handleServicesProvided(appDialog)()}
				startIcon={<AddCircleIcon />}
			>
				Criar
			</Button>
		</Box>
	);
};

const DoneVsExpectedOverall = ({
	treatment,
	servicesProvided,
}: {
	treatment: Treatment;
	servicesProvided: Array<ServiceProvided>;
}) => {
	if (!treatment) return null;
	const treatmentWithServicesProvided = {
		...treatment,
		treatment_prescription: [
			{
				...treatment.treatment_prescription?.[0],
				[SERVICES_PROVIDED_ENTITY]: servicesProvided,
			},
		],
		sessions: treatment.sessions.map((session) => ({
			...session,
			service: { ...session.service, max_duration: 0 },
		})),

	};
	const doneVsExpected = makeDoneVsExpectedSessionOverall(treatmentWithServicesProvided);

	return (
		<Box display="flex" flexDirection="row" justifyContent="center" width="fit-content" gap={4} my={2}>
			<Typography variant="body1" color="primary.main">
				Sessões Realizadas / Esperadas (Geral):
			</Typography>
			{Object.entries(doneVsExpected).map(([id, { done, expected }]) => {
				const serviceName = getLabelByServiceId(Number(id));
				const color = (done || done === 0) && done !== expected ? "red" : "default";

				return (
					<Box key={id}>
						<Typography variant="body1" color={color ? color : "default"}>
							<Box component="span" color={"text.primary"}>
								{serviceName}
							</Box>
							{`: ${done} / ${expected}`}
						</Typography>
					</Box>
				);
			})}
		</Box>
	);
};
